import React, { useState, useEffect } from 'react';
import { Menu, X, Globe } from 'lucide-react';
import { Logo } from '../atoms/Logo';

export interface NavItem {
  name: string;
  path: string;
}

export interface AppShellProps {
  /** Brand name displayed next to logo */
  brandName: string;
  /** Navigation items */
  navItems: NavItem[];
  /** Footer navigation items */
  footerNavItems?: NavItem[];
  /** Current pathname for active state */
  currentPath: string;
  /** Current language */
  language: 'tr' | 'en';
  /** Language toggle handler */
  onLanguageToggle: () => void;
  /** CTA button text */
  ctaText?: string;
  /** CTA link path */
  ctaPath?: string;
  /** Optional CTA click handler */
  onCtaClick?: () => void;
  /** Footer description text */
  footerDescription: React.ReactNode;
  /** Quick links section title */
  quickLinksTitle: string;
  /** Copyright text */
  copyrightText: string;
  /** "Designed in" text */
  designedInText: string;
  /** Main content */
  children: React.ReactNode;
  /** Render function for navigation links (to support NavLink or Link from router) */
  renderNavLink: (item: NavItem, isActive: boolean, className: string) => React.ReactNode;
  /** Render function for CTA link */
  renderCtaLink: (text: string, path: string, className: string, onClick?: () => void) => React.ReactNode;
  /** Render function for footer links */
  renderFooterLink: (item: NavItem, className: string) => React.ReactNode;
  /** Render function for home link (logo) */
  renderHomeLink: (children: React.ReactNode, className: string) => React.ReactNode;
}

/**
 * Application shell organism with header, footer, and navigation
 *
 * This is a presentational component that receives render props for
 * router-specific link components (NavLink, Link, etc.)
 */
export const AppShell: React.FC<AppShellProps> = ({
  brandName,
  navItems,
  footerNavItems,
  currentPath,
  language,
  onLanguageToggle,
  ctaText,
  ctaPath,
  onCtaClick,
  footerDescription,
  quickLinksTitle,
  copyrightText,
  designedInText,
  children,
  renderNavLink,
  renderCtaLink,
  renderFooterLink,
  renderHomeLink,
}) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    window.scrollTo(0, 0);
  }, [currentPath]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [isMobileMenuOpen]);

  const isActive = (path: string) => {
    if (path === '/') return currentPath === '/';
    return currentPath.startsWith(path);
  };

  const handleCtaClick = onCtaClick
    ? () => {
        setIsMobileMenuOpen(false);
        onCtaClick();
      }
    : undefined;

  return (
    <div className="flex flex-col min-h-screen bg-dark-900 text-slate-50 font-sans selection:bg-brand-500 selection:text-white">
      {/* Navbar */}
      <header
        className={`fixed w-full z-[100] transition-all duration-300 border-b ${
          isScrolled
            ? 'bg-dark-900/90 backdrop-blur-md border-slate-800 py-3'
            : 'bg-transparent border-transparent py-6'
        }`}
      >
        <div className="container mx-auto px-6 flex justify-between items-center">
          {renderHomeLink(
            <>
              <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-lg shadow-brand-500/20 group-hover:scale-105 transition-transform">
                <Logo className="w-6 h-6" />
              </div>
              <span className="text-xl font-bold tracking-tight text-white">
                {brandName}
              </span>
            </>,
            'flex items-center gap-3 group relative z-50'
          )}

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-6">
            {navItems.map((link) =>
              renderNavLink(
                link,
                isActive(link.path),
                `text-sm font-medium transition-colors hover:text-brand-400 ${
                  isActive(link.path) ? 'text-brand-500' : 'text-slate-300'
                }`
              )
            )}

            {/* Language Switcher Desktop */}
            <button
              onClick={onLanguageToggle}
              className="text-slate-400 hover:text-white transition-colors text-sm font-semibold px-2 py-1 flex items-center gap-1"
            >
              <Globe size={16} />
              {language === 'tr' ? 'EN' : 'TR'}
            </button>

            {ctaText && ctaPath
              ? renderCtaLink(
                  ctaText,
                  ctaPath,
                  'relative isolate overflow-hidden px-5 py-2.5 bg-white text-dark-900 text-sm font-bold rounded-lg transition-all ring-1 ring-white/80 hover:-translate-y-0.5 hover:bg-slate-100 shadow-lg shadow-white/10',
                  handleCtaClick
                )
              : null}
          </nav>

          {/* Mobile Actions */}
          <div className="md:hidden flex items-center gap-4 relative z-50">
            {/* Language Switcher Mobile */}
            <button
              onClick={onLanguageToggle}
              className="text-white font-bold text-sm bg-slate-800 px-3 py-1.5 rounded-lg border border-slate-700"
            >
              {language === 'tr' ? 'EN' : 'TR'}
            </button>

            <button
              className="text-white p-2"
              aria-label={language === 'tr'
                ? (isMobileMenuOpen ? 'Menüyü kapat' : 'Menüyü aç')
                : (isMobileMenuOpen ? 'Close menu' : 'Open menu')}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        <div
          className={`md:hidden fixed inset-0 bg-dark-900/95 backdrop-blur-xl z-40 transition-all duration-300 flex flex-col items-center justify-center ${
            isMobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'
          }`}
        >
          <nav className="flex flex-col items-center gap-8">
            {navItems.map((link) =>
              renderNavLink(
                link,
                isActive(link.path),
                `text-2xl font-bold transition-all ${
                  isActive(link.path) ? 'text-brand-500 scale-110' : 'text-white hover:text-brand-400'
                }`
              )
            )}
            {ctaText && ctaPath
              ? renderCtaLink(
                  ctaText,
                  ctaPath,
                  'relative isolate overflow-hidden mt-8 px-8 py-4 bg-white text-dark-900 font-bold rounded-xl transition-all ring-1 ring-white/80 hover:-translate-y-0.5 hover:bg-slate-100 shadow-xl shadow-white/10',
                  handleCtaClick
                )
              : null}
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-grow">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-dark-900 pt-20 pb-10 border-t border-slate-800">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-12 mb-16">
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center">
                  <Logo className="w-5 h-5" />
                </div>
                <span className="text-xl font-bold text-white">{brandName}</span>
              </div>
              <p className="text-slate-400 max-w-sm mb-6">
                {footerDescription}
              </p>
            </div>

            <div>
              <h4 className="text-white font-bold mb-6">{quickLinksTitle}</h4>
              <ul className="space-y-4">
                {(footerNavItems ?? navItems.slice(0, 4)).map((item) => (
                  <li key={item.path}>
                    {renderFooterLink(item, 'text-slate-400 hover:text-brand-500 transition-colors')}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="border-t border-slate-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-400 text-sm">
              {copyrightText}
            </p>
            <div className="flex items-center gap-4">
              <p className="text-slate-400 text-xs">
                {designedInText}
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AppShell;
